/**
 * ============================================================================
 * heroGreenBar.js - Gestione Green Bar Position (Hero Sticky)
 * ============================================================================
 * 
 * Sistema di posizionamento dinamico della green bar (barra verde sotto hero):
 * 
 * COMPORTAMENTI:
 * • Hero VISIBILE (bottom > headerH):
 *   → Barra in position: absolute al bordo inferiore dell'hero
 *   → Segue il flusso del documento
 * 
 * • Hero NASCOSTO (bottom ≤ headerH):
 *   → Barra diventa sticky: position: fixed sotto l'header
 *   → z-index: 150 per stare sopra i contenuti
 * 
 * • Footer IN VISTA (scrollY > 0 && footer top ≤ viewport):
 *   → Barra scompare (opacity: 0, pointer-events: none)
 * 
 * MECCANISMI DI CALCOLO:
 * • Header height: calcolato dinamicamente per supportare responsive design
 * • Media queries: ascoltate per ricalcolare al cambio breakpoint
 * • ResizeObserver: monitora header, hero e footer per adattamenti immediati
 * • Hero fluid height: supporta clamp() e max-height dinamici
 * 
 * EVENTO CUSTOM:
 * • 'greenbar:ready': emesso UNA VOLTA dopo il primo posizionamento
 *   → Sincronizza i loghi (useFoundationLogoVisibility) con la green bar
 * 
 * PERFORMANCE:
 * • Scroll event: passive=true (non blocca il rendering)
 * • Resize observer: debounced con setTimeout 16ms
 * • Fire-once logic: 'greenbar:ready' emesso una sola volta
 * ============================================================================
 */

import { onMediaChange, offMediaChange, createResizeObserver } from '@/utils/legacySupport'

let noop = () => {}

/**
 * setupHeroGreenBar - Inizializza il sistema di posizionamento della green bar
 * 
 * @returns {Function} Funzione di cleanup da chiamare con disposeHeroGreenBar()
 * 
 * FLUSSO OPERATIVO:
 * 1. Valida gli elementi necessari nel DOM (.header-base, .section-hero, .site-header)
 * 2. Calcola inizialmente la posizione della barra
 * 3. Imposta listener per: scroll, resize, media queries, load event
 * 4. Osserva cambiamenti dimensionali di header e footer
 * 5. Emette 'greenbar:ready' UNA VOLTA per sincronizzare i loghi
 * 
 * LISTENER E OBSERVER:
 * • scroll: aggiornamento in real-time della posizione (passive)
 * • resize: adattamento a cambi viewport
 * • media query: ricalcolo su breakpoint changes
 * • ResizeObserver: header, hero e footer monitorati per resize dinamici
 * • window 'load': assicura posizionamento corretto dopo caricamento risorse
 * • transitionend: ricalcola se l'header cambia altezza durante transizioni CSS
 */
export function setupHeroGreenBar () {
  const headerBase = document.querySelector('.header-base')
  const hero = document.querySelector('.section-hero')
  const header = document.querySelector('.site-header')
  const footer = document.querySelector('footer.site-footer')

  // Validazione elementi obbligatori
  if (!headerBase || !hero || !header) {
    console.warn('setupHeroGreenBar: Elementi necessari non trovati (.header-base, .section-hero, .site-header)')
    return noop
  }

  // Transizione smooth per opacity quando appare/scompare
  headerBase.style.transition = 'opacity 0.3s ease-in-out'

  /**
   * Ottiene l'altezza attuale dell'header
   * @returns {number} Altezza in pixel
   */
  const getHeaderHeight = () => header.offsetHeight || 0

  /**
   * Verifica se il footer è visibile nel viewport
   * @returns {boolean} true se utente ha scrollato E footer è in vista
   */
  const isFooterVisible = () => {
    if (!footer) return false
    const { top } = footer.getBoundingClientRect()
    const scrolled = window.scrollY > 0
    const footerInView = top <= window.innerHeight
    return scrolled && footerInView
  }

  // Stato per emettere 'greenbar:ready' una sola volta
  let didFireReady = false

  /**
   * Emette evento custom 'greenbar:ready' una sola volta
   * Usato da useFoundationLogoVisibility per sincronizzare i loghi
   */
  const fireReadyOnce = () => {
    if (didFireReady) return
    didFireReady = true
    document.dispatchEvent(new Event('greenbar:ready'))
  }

  /**
   * Aggiorna la posizione della barra in base allo scroll e alla visibilità dell'hero
   * Logica principale del sistema
   */
  const updatePos = () => {
    const headerH = getHeaderHeight()
    const { bottom } = hero.getBoundingClientRect()

    if (isFooterVisible()) {
      // Footer visibile → barra scompare
      headerBase.style.opacity = '0'
      headerBase.style.pointerEvents = 'none'
    } else {
      // Footer non visibile → barra visible
      headerBase.style.opacity = '1'
      headerBase.style.pointerEvents = 'auto'

      if (bottom > headerH) {
        // CASO 1: Hero ancora visibile → barra in absolute al suo bordo
        headerBase.style.position = 'absolute'
        headerBase.style.top      = `${bottom}px`
        headerBase.style.left     = '0'
        headerBase.style.width    = '100%'
        headerBase.style.zIndex   = '0'
      } else {
        // CASO 2: Hero nascosto → barra fixed sotto l'header
        headerBase.style.position = 'fixed'
        headerBase.style.top      = `${headerH}px`
        headerBase.style.left     = '0'
        headerBase.style.width    = '100%'
        headerBase.style.zIndex   = '150'
      }
    }

    // Emette 'greenbar:ready' dopo il PRIMO posizionamento
    fireReadyOnce()
  }

  // ─────────────────────────────────────────────────────────────
  // EVENT LISTENERS
  // ─────────────────────────────────────────────────────────────

  // Scroll: aggiornamento in real-time (passive: non blocca rendering)
  window.addEventListener('scroll', updatePos, { passive: true })

  // Resize window: ricalcola quando viewport cambia
  window.addEventListener('resize', updatePos)

  // TransitionEnd header: ricalcola se header cambia altezza con transizioni CSS
  header.addEventListener('transitionend', updatePos)

  // Media queries: ricalcola al cambio di breakpoint
  const mqls = [
    window.matchMedia('(max-width: 1300px)'),
    window.matchMedia('(max-width: 1024px)'),
    window.matchMedia('(max-width: 768px)'),
    window.matchMedia('(max-width: 480px)'),
    window.matchMedia('(max-width: 360px)')
  ]
  // `addEventListener` su MediaQueryList esiste solo da iOS 14: l'helper usa
  // quello dove c'e' e ricade su `addListener` sui browser precedenti.
  mqls.forEach(mql => onMediaChange(mql, updatePos))

  // ─────────────────────────────────────────────────────────────
  // RESIZE OBSERVERS (per dimensioni dinamiche)
  // ─────────────────────────────────────────────────────────────

  // Osserva header: debounced a 16ms per performance
  // ResizeObserver esiste da iOS 13.4: sotto quella versione l'helper
  // restituisce null invece di lanciare, e si prosegue senza observer.
  // L'aggiornamento su `resize` e sullo scroll resta attivo.
  const headerObserver = createResizeObserver(() => {
    clearTimeout(headerObserver.timeoutId)
    headerObserver.timeoutId = setTimeout(updatePos, 16)
  })
  if (headerObserver) headerObserver.observe(header)

  // Osserva hero: debounced a 16ms per performance
  const heroObserver = createResizeObserver(() => {
    clearTimeout(heroObserver.timeoutId)
    heroObserver.timeoutId = setTimeout(updatePos, 16)
  })
  if (heroObserver) heroObserver.observe(hero)

  // Osserva footer: debounced a 16ms per performance
  let footerObserver
  if (footer) {
    footerObserver = createResizeObserver(() => {
      clearTimeout(footerObserver.timeoutId)
      footerObserver.timeoutId = setTimeout(updatePos, 16)
    })
    if (footerObserver) footerObserver.observe(footer)
  }

  // ─────────────────────────────────────────────────────────────
  // STATO INIZIALE
  // ─────────────────────────────────────────────────────────────

  // Posizionamento immediato
  updatePos()

  // Riposizionamento dopo caricamento di immagini/font per layout stabile
  window.addEventListener('load', () => {
    updatePos()
  }, { once: true })

  // ─────────────────────────────────────────────────────────────
  // CLEANUP FUNCTION
  // ─────────────────────────────────────────────────────────────

  /**
   * Rimuove tutti i listener e observer
   * Ripristina gli stili inline della barra
   */
  const dispose = () => {
    window.removeEventListener('scroll', updatePos)
    window.removeEventListener('resize', updatePos)
    header.removeEventListener('transitionend', updatePos)
    mqls.forEach(mql => offMediaChange(mql, updatePos))
    if (headerObserver) headerObserver.disconnect()
    if (heroObserver) heroObserver.disconnect()
    if (footerObserver) footerObserver.disconnect()
    headerBase.removeAttribute('style')
  }

  noop = dispose
  return dispose
}

/**
 * disposeHeroGreenBar - Cleanup wrapper
 * 
 * Chiama la funzione dispose ritornata da setupHeroGreenBar()
 * Resetta il noop interno per next setup
 */
export function disposeHeroGreenBar () {
  noop()
  noop = () => {}
}